import { GVL } from '@iabtcf/core'
import { CmpApi } from '@iabtcf/cmpapi'
// using the following until the bug is fixed in @iabtcf/stub
// https://raw.githubusercontent.com/InteractiveAdvertisingBureau/iabtcf-es/95c5585d26e751b173e98ce830da2fa2dd5e5368/modules/stub/src/stub.js
// https://github.com/InteractiveAdvertisingBureau/iabtcf-es/pull/192
import cmpstub from './stub'
// import cmpstub from '@iabtcf/stub'
import { createLog } from './logger'
import { createTcModel } from './tcModel'
import { ACMModel } from './ACMModel'

const log = createLog()

function parseCvl (cvl) {
  // eslint-disable-next-line camelcase
  return Object.values(cvl).reduce((acc, { provider_id, provider_name, policy_url }) => ({
    ...acc,
    [provider_id]: {
      id: provider_id,
      name: provider_name,
      policyUrl: policy_url,
      description: ''
    }

  }), {})
}

class Cmp {
  constructor ({ tcModel, cvlJson = {}, tcString, storeTcString, customVendorConsents = {}, persist, persistCustomVendors, ui, uuid, gvlVersion, gvlUiVersion }) {
    cmpstub()
    this.tcModel = tcModel
    this.hasUserSelectedTheirPreference = !!tcString
    this.notifyModelUpdate = this.notifyModelUpdate.bind(this)
    this.isReady = this.isReady.bind(this)
    this.openUi = this.openUi.bind(this)
    this.notifyUsersPreferences = this.notifyUsersPreferences.bind(this)
    this.hasUserConsentedToAll = this.hasUserConsentedToAll.bind(this)
    this.updateConset = this.updateConsent.bind(this)
    this.hasUserConsentedToVendors = this.hasUserConsentedToVendors.bind(this)
    this.getGvl = this.getGvl.bind(this)
    this.consentToAll = this.consentToAll.bind(this)
    this.dissentToAll = this.dissentToAll.bind(this)
    this.updateConsent = this.updateConsent.bind(this)
    this.reloadTcModel = this.reloadTcModel.bind(this)
    this.levelChanged = this.levelChanged.bind(this)
    this.storeTcString = storeTcString
    this.persist = persist
    this.ui = ui
    this.uuid = uuid
    this.lastKnownConsentScreen = undefined
    this.gvlVersion = gvlVersion
    this.gvlUiVersion = gvlUiVersion
    this.acmModel = new ACMModel({ cvlJson, acmModel: customVendorConsents })
    this.customVendors = {
      cvlJson,
      cvl: parseCvl(cvlJson),
      consents: customVendorConsents || {}
    }
    this.persistCustomVendors = persistCustomVendors

    const validTCDataP = () => new Promise(resolve => {
      this.tcModel.gvlIsReady().then(() => {
        let resolved = false
        const done = (tcData) => {
          if (!resolved) {
            resolved = true
            resolve(tcData)
          }
        }
        global.__tcfapi('addEventListener', 2, tcData => {
          if (tcData.tcString.length) {
            global.__tcfapi('removeEventListener', 2, () => {}, tcData.listenerId)
            done(tcData)
          }
        })
        global.__tcfapi('getTCData', 2, tcData => {
          if (tcData.tcString.length) {
            done(tcData)
          }
        })
      })
    })

    this.api = new CmpApi(27, 3, true, {
      hasUserConsentedToAll: async callback => {
        await this.tcModel.gvlIsReady()
        callback(this.hasUserConsentedToAll())
      },
      hasUserDissentedToAll: async callback => {
        await this.tcModel.gvlIsReady()
        callback(this.hasUserDissentedToAll())
      },
      hasUserConsentedToVendors: async (callback, ids) => {
        await this.tcModel.gvlIsReady()
        callback(this.hasUserConsentedToVendors(ids))
      },
      getPurposesByVendorMap: async (callback) => {
        await this.tcModel.gvlIsReady()
        const entries = Object.entries(this.tcModel.byPurposeVendorMap())
        const purposesByVendorMap = entries
          .reduce((acc, [purpose, { consent: vendorIds }]) => {
            vendorIds.forEach(vendorId => (acc[vendorId] = acc[vendorId] || []).push(purpose))
            return acc
          }, {})
        callback(purposesByVendorMap)
      },
      getCustomVendorsConsents: async (callback) => {
        await this.tcModel.gvlIsReady()
        callback(this.getCustomVendorsConsents())
      },
      getConsentDegree: async (callback) => {
        callback(await this.getConsentDegree())
      },
      setUserId: (callback, uuid) => {
        this.uuid = uuid
        return callback(true)
      },
      openUi: (callback, { uiLevel, uuid = this.uuid }) => {
        this.uuid = uuid
        this.openUi(uiLevel)
        callback(true)
      },
      getValidTCData: async callback => {
        try {
          callback(await validTCDataP(), true)
        } catch (e) {
          log(e)
          callback(null, false)
        }
      },
      consentedVendors: async callback => {
        callback(await this.consentedVendors())
      },
      consentedVendorsDetails: async callback => {
        callback(await this.consentedVendorsDetails())
      }
    })

    // todo: REMOVE
    global.model = () => this.tcModel.model
  }

  getGvl () {
    return this.tcModel.gvl
  }

  async notifyModelUpdate () {
    const tcString = this.tcModel.tcString()
    this.hasUserSelectedTheirPreference = true
    const consentDegree = await this.getConsentDegree()
    const consents = this.acmModel.getConsents()
    await this.persist.saveTCString(this.uuid, tcString, consentDegree, this.persistCustomVendors ? consents : undefined)
    this.api.update(tcString, false, this.acmModel.acmString)
  }

  updateConsent (data) {
    log('data', data)

    const formConsentAndLegInt = (pref, keyPrefix) => {
      const consentKey = `${keyPrefix}Consents`
      const legIntKey = `${keyPrefix}LegitimateInterests`
      return Object.entries(pref).reduce((acc, [id, userPreference]) => {
        const { consent, legInterest } = userPreference
        const _id = parseInt(id)
        if (consent) {
          acc[consentKey].push(_id)
        }
        if (legInterest) {
          acc[legIntKey].push(_id)
        }
        return acc
      }, { [consentKey]: [], [legIntKey]: [] })
    }

    const { purposes = {}, specialFeatures = {}, vendors = {}, nonTcfVendors = {} } = data
    const { vendorConsents, vendorLegitimateInterests } = formConsentAndLegInt(vendors, 'vendor')
    const { purposeConsents, purposeLegitimateInterests } = formConsentAndLegInt(purposes, 'purpose')
    const specialFeatureOptins = Object.entries(specialFeatures).reduce((acc, [specialFeatureId, userPreference]) => {
      const id = parseInt(specialFeatureId)
      if (userPreference.optIn) {
        acc.push(id)
      }
      return acc
    }, [])

    this.acmModel.updateModel(nonTcfVendors)
    const options = { vendorConsents, vendorLegitimateInterests, purposeConsents, purposeLegitimateInterests, specialFeatureOptins, consentScreen: this.lastKnownConsentScreen }
    log('options:', options)
    this.tcModel.updateUserPreferences(options)
    this.notifyModelUpdate()
    log('model updates', vendorConsents, vendorLegitimateInterests, purposeConsents, purposeLegitimateInterests, this.acmModel.getConsents())
  }

  consentToAll () {
    log('user consenting to all')
    this.acmModel.consentToAll()
    this.tcModel.consentAll({ consentScreen: this.lastKnownConsentScreen })
    this.notifyModelUpdate()
  }

  dissentToAll () {
    log('user dissenting to all')
    this.acmModel.dissentToAll()
    this.tcModel.dissentAll({ consentScreen: this.lastKnownConsentScreen })
    this.notifyModelUpdate()
  }

  levelChanged ({ currentLevel }) {
    try {
      this.setCurrentLevel(currentLevel)
      log('last known consent screen:', this.lastKnownConsentScreen)
    } catch (e) {
      log(e.message)
    }
  }

  setCurrentLevel (currentLevel) {
    const screen = currentLevel.match(/\d/)
    if (typeof screen[0] === 'undefined') {
      throw new Error('Unknown level: ' + currentLevel)
    }
    this.lastKnownConsentScreen = screen[0]
  }

  isReady () {
    return this.tcModel.gvlIsReady()
  }

  hasUserConsentedToAll () {
    if (!this.hasUserSelectedTheirPreference) return false
    return this.tcModel.hasUserConsentedToAll() && this.acmModel.hasUserConsentedToAll()
  }

  hasUserDissentedToAll () {
    if (!this.hasUserSelectedTheirPreference) return false
    return this.tcModel.hasUserDissentedToAll() && this.acmModel.hasUserDissentedToAll()
  }

  hasUserConsentedToVendors (ids) {
    return this.tcModel.hasUserConsentedToVendors(ids)
  }

  getCustomVendorsConsents () {
    return this.acmModel.getConsents()
  }

  async getConsentDegree () {
    await this.tcModel.gvlIsReady()
    return this.hasUserSelectedTheirPreference ? this.hasUserConsentedToAll() && 'yes' || this.hasUserDissentedToAll() && 'no' || 'partial' : undefined
  }

  async consentedVendors () {
    await this.tcModel.gvlIsReady()
    return this.tcModel.consentedVendors()
  }

  async consentedVendorsDetails () {
    await this.tcModel.gvlIsReady()
    return this.tcModel.consentedVendorsDetails()
  }

  async openUi (uiLevel) {
    if (this.gvlVersion !== this.gvlUiVersion) {
      const url = `//cmp.dmgmediaprivacy.co.uk/gvl/${this.gvlUiVersion}/vendor-list.json`
      const response = await fetch(url)
      const gvlJson = await response.json()
      const tcModel = await createTcModel({ gvl: new GVL(gvlJson), oldModel: this.tcModel })
      this.tcModel = tcModel
      this.gvlVersion = this.gvlUiVersion
    }

    const consentString = this.hasUserSelectedTheirPreference ? this.tcModel.tcString() : ''
    const cmpModel = {
      tcModel: this.tcModel.model,
      uuid: this.uuid,
      hasUserSelectedTheirPreference: this.hasUserSelectedTheirPreference,
      customVendors: this.acmModel.getUiModel()
    }
    const cmpUpdateHandlers = {
      reloadTcModel: this.reloadTcModel,
      updateConsent: this.updateConsent,
      consentToAll: this.consentToAll,
      dissentToAll: this.dissentToAll,
      levelChanged: this.levelChanged
    }

    log('opening UI')
    this.setCurrentLevel(uiLevel)
    this.ui.open(uiLevel, { cmpModel, cmpUpdateHandlers })
    this.api.update(consentString, true, this.acmModel.acmString)
  }

  notifyUsersPreferences () {
    const consentString = this.hasUserSelectedTheirPreference ? this.tcModel.tcString() : ''
    log('notifying users')
    this.api.update(consentString, false, this.acmModel.acmString)
  }

  reloadTcModel (tcString) {
    this.tcModel.reload(tcString)
    this.isReady().then(() => {
      this.hasUserSelectedTheirPreference = true
      this.notifyUsersPreferences()
    })
  }

  setUuid (uuid) {
    this.uuid = uuid
  }
}

export async function createCmp ({ gvlJson, cvlJson = {}, tcString, storeTcString, customVendorConsents = {}, persist, persistCustomVendors, ui, uuid, gvlVersion, gvlUiVersion }) {
  const gvl = new GVL(gvlJson)
  const tcModel = await createTcModel({ gvl, tcString })
  return new Cmp({ gvl, tcModel, cvlJson, tcString, storeTcString, customVendorConsents, persist, persistCustomVendors, ui, uuid, gvlVersion, gvlUiVersion })
}
